import type { ReactNode } from 'react';
import { C, DEGRADE_ACTION, POLICE_TITRE } from '../theme';
import { Micro, NavAujourdhui, NavBoite, NavPlanning, NavColette } from '../icones';
import { naviguer, CHEMIN_CONFIDENTIALITE, CHEMIN_DEMO } from '../routeur';

/** Lien interne : navigation sans rechargement, mais vrai `href` pour le clic-droit,
 *  l'ouverture dans un onglet et les moteurs. */
export function Lien({
  vers,
  children,
  className,
}: {
  vers: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <a
      href={vers}
      className={className}
      onClick={(e) => {
        // On laisse le navigateur faire son travail pour ctrl/cmd-clic et clic milieu.
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        naviguer(vers);
      }}
    >
      {children}
    </a>
  );
}

export function EnTete({ actionPrincipale }: { actionPrincipale?: ReactNode }) {
  return (
    <header className="v-nav">
      <div className="v-contenu v-nav__interieur">
        <Lien vers="/" className="v-marque">
          <span className="v-marque__pastille">
            <Micro taille={18} couleur="#FFFFFF" />
          </span>
          Colette
        </Lien>
        {actionPrincipale}
      </div>
    </header>
  );
}

export function Pied() {
  return (
    <footer className="v-pied">
      <div className="v-contenu v-pied__interieur">
        <span>Colette · version d'essai privée</span>
        <nav style={{ display: 'flex', gap: 'var(--e5)', flexWrap: 'wrap' }}>
          <Lien vers={CHEMIN_DEMO}>Comment ça marche</Lien>
          <Lien vers={CHEMIN_CONFIDENTIALITE}>Confidentialité</Lien>
          <Lien vers="/app">Ouvrir Colette</Lien>
        </nav>
      </div>
    </footer>
  );
}

// --- Maquette de telephone ---------------------------------------------------
//
// Illustration, pas capture d'ecran : elle reprend les composants et la palette de
// l'application reelle, en reduit. Toute evolution de l'app doit s'y refleter, sinon la
// vitrine promet un produit qui n'existe pas.

export function TelephoneApercu() {
  return (
    <div className="v-tel" role="img" aria-label="Aperçu de l'écran Aujourd'hui de Colette">
      <div className="v-tel__ecran">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 6 }}>
          <div
            style={{
              fontSize: 9,
              fontWeight: 800,
              letterSpacing: 1.2,
              textTransform: 'uppercase',
              color: C.tertiaire,
            }}
          >
            Mercredi 26 août
          </div>
          <div
            style={{
              fontFamily: POLICE_TITRE,
              fontWeight: 200,
              fontSize: 30,
              lineHeight: 1.04,
              color: C.encre,
            }}
          >
            Bonjour
            <br />
            Marianne
          </div>
          <div
            style={{ fontFamily: POLICE_TITRE, fontWeight: 300, fontSize: 12, color: C.secondaire }}
          >
            Journée chargée, mais sous contrôle.
          </div>
        </div>

        <MiniPilule teinte="#8B7BE8" titre="Réunion équipe" meta="09:20" compteur="2" />
        <MiniPilule teinte="#F27059" titre="Sortie scolaire" meta="Demain · 08:15" compteur="3" />
        <MiniPilule teinte="#3FBFA0" titre="Piscine de Lila" meta="17:00" />
        <MiniPilule teinte="#E8C34A" titre="Signer l’autorisation" meta="À ne pas oublier" />
      </div>

      <div className="v-tel__barre">
        <NavAujourdhui taille={18} couleur={C.corailFixe} />
        <NavBoite taille={18} couleur={C.tertiaireFixe} />
        <span
          style={{
            width: 38,
            height: 38,
            borderRadius: '50%',
            background: DEGRADE_ACTION,
            display: 'grid',
            placeItems: 'center',
            marginTop: -18,
            boxShadow: '0 5px 12px rgba(242,112,89,0.4)',
          }}
        >
          <Micro taille={18} couleur="#FFFFFF" />
        </span>
        <NavPlanning taille={18} couleur={C.tertiaireFixe} />
        <NavColette taille={18} couleur={C.tertiaireFixe} />
      </div>
    </div>
  );
}

/** La pilule de l'ecran Aujourd'hui, en reduction : c'est bien le produit qu'on montre. */
function MiniPilule({
  teinte,
  titre,
  meta,
  compteur,
}: {
  teinte: string;
  titre: string;
  meta: string;
  compteur?: string;
}) {
  return (
    <div
      style={{
        background: C.carte,
        border: `1px solid ${C.bordure}`,
        borderRadius: 999,
        padding: '8px 11px',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
      }}
    >
      <span
        style={{ width: 6, height: 6, borderRadius: '50%', background: teinte, flexShrink: 0 }}
      />
      <span style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: C.encre,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {titre}
        </span>
        <span style={{ fontSize: 9, color: C.tertiaire }}>{meta}</span>
      </span>
      {compteur !== undefined ? (
        <span
          style={{
            background: C.accentDoux,
            color: C.accent,
            borderRadius: 999,
            padding: '2px 6px',
            fontSize: 9,
            fontWeight: 800,
            flexShrink: 0,
          }}
        >
          {compteur}
        </span>
      ) : null}
    </div>
  );
}
